import { Button, Card, Skeleton } from "@attmos/flowstack-ui";
import { ArrowRight, Send } from "lucide-react";
import * as React from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { useArtifacts, type Artifact } from "@/api/artifacts";
import { ApiError } from "@/api/client";
import { useUploadMolecule } from "@/api/molecules";
import { useProject, useRememberProject } from "@/api/projects";
import { useExecutionMode } from "@/api/readyz";
import { useSubmitRun, type ResourceOverride } from "@/api/submission";
import { useTileManifest, useTiles } from "@/api/tiles";
import { EmptyState } from "@/components/layout/EmptyState";
import { PageHeader } from "@/components/layout/PageHeader";
import { ArtifactInputPicker, type InputPort } from "@/components/submission/ArtifactInputPicker";
import { SelectedInputPreview } from "@/components/submission/SelectedInputPreview";
import { CostPreviewPanel } from "@/components/submission/CostPreviewPanel";
import { ExecutionModeNotice } from "@/components/submission/ExecutionModeNotice";
import { FailedUpstreamBanner } from "@/components/submission/FailedUpstreamBanner";
import { ManifestForm } from "@/components/submission/ManifestForm";
import {
  ResourceClassControl,
  parseDeclaredResources,
  resourceCapability,
} from "@/components/submission/ResourceClassControl";
import { TilePicker } from "@/components/submission/TilePicker";

interface RouteParams {
  slug?: string;
  [key: string]: string | undefined;
}

/**
 * Manifest-driven Tile submission page.
 *
 * Three-column layout on desktop: Tile picker (left) → manifest form
 * (center) → CostPreviewPanel (right rail, sticky). Stacks vertically
 * on mobile.
 *
 * Selection round-trips through ?tile= so a link to a pre-selected
 * Tile bookmarks cleanly. Parameter edits never reach the URL — they
 * only live in component state until submission.
 */
export function SubmitTilePage(): JSX.Element {
  const { slug = "" } = useParams<RouteParams>();
  const project = useProject(slug);
  useRememberProject(slug);
  const tiles = useTiles();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTile = searchParams.get("tile");
  const manifest = useTileManifest(selectedTile ?? "");
  const artifacts = useArtifacts();
  const submit = useSubmitRun();
  const uploadMolecule = useUploadMolecule();
  // What this deployment can actually do with a submission (#1174). Read
  // before anything is submitted, and never used to gate the Submit button.
  const executionMode = useExecutionMode();
  const navigate = useNavigate();
  const [values, setValues] = React.useState<Record<string, unknown>>({});
  const [selectedArtifacts, setSelectedArtifacts] = React.useState<Record<string, string>>({});
  // Submit-time CPU/GPU resource-class override (flowstack#620).
  // ``undefined`` = submit with the tile's declared resources (the
  // default-safe path); the ResourceClassControl emits an override only
  // when the user changes the class or a size field.
  const [resourceOverride, setResourceOverride] = React.useState<ResourceOverride | undefined>(
    undefined
  );
  // The override is keyed to the *identity* of the failed-selection
  // set it acknowledges (Rupert PR-300 r1). Storing the
  // ``acknowledgedFailedHashesKey`` instead of a bare boolean makes
  // the acknowledgement atomic: if the user swaps to a different
  // failed artifact, the stored key no longer matches the current
  // failed set and the override is implicitly null. No transient
  // render window where the old-true state survives.
  const [acknowledgedFailedHashesKey, setAcknowledgedFailedHashesKey] = React.useState<
    string | null
  >(null);

  // When the Tile changes, reset every per-Tile selection so we
  // don't bleed stale values across Tiles.
  React.useEffect(() => {
    setValues({});
    setSelectedArtifacts({});
    setAcknowledgedFailedHashesKey(null);
    setResourceOverride(undefined);
  }, [selectedTile]);

  // Defensive shape check on the manifest payload (per Rupert PR-300
  // r0): only treat entries whose `type` is a string as valid
  // InputPorts. If the manifest schema's `inputs` block ever drifts
  // (array form, missing type, etc), this returns an empty set instead
  // of silently filtering everything in the catalog to zero matches.
  const manifestInputs = React.useMemo(() => {
    const raw = (manifest.data?.manifest as { inputs?: unknown } | undefined)?.inputs;
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
    const out: Record<string, InputPort> = {};
    for (const [name, value] of Object.entries(raw as Record<string, unknown>)) {
      if (!value || typeof value !== "object") continue;
      const v = value as {
        type?: unknown;
        cardinality?: unknown;
        required?: unknown;
        description?: unknown;
      };
      if (typeof v.type !== "string") continue;
      out[name] = {
        type: v.type,
        cardinality: v.cardinality === "many" ? "many" : "one",
        required: typeof v.required === "boolean" ? v.required : true,
        description: typeof v.description === "string" ? v.description : undefined,
      };
    }
    return out;
  }, [manifest.data]);
  const availableArtifacts = React.useMemo(() => artifacts.data ?? [], [artifacts.data]);

  // flowstack#620: the tile's declared resources drive the pre-filled
  // resource-class control; the capability (from the base gpu + any
  // ``resource_overrides_required_when`` rule) tells us which classes
  // the tile can run on so the UI disables exactly what the API rejects.
  const declaredResources = React.useMemo(
    () => parseDeclaredResources(manifest.data?.manifest),
    [manifest.data]
  );
  const resourceCap = React.useMemo(
    () => resourceCapability(manifest.data?.manifest),
    [manifest.data]
  );

  // Resolve every currently-selected hash to its Artifact record so we
  // can find the failed-tagged ones for the banner. Empty when no
  // artifact is selected or when the hash isn't in the current catalog
  // page (the picker surfaces that case separately).
  const failedSelections = React.useMemo(() => {
    const out: { portName: string; artifact: Artifact }[] = [];
    for (const [portName, hash] of Object.entries(selectedArtifacts)) {
      const a = availableArtifacts.find((x) => x.content_hash === hash);
      if (a && a.scientific_status === "failed") {
        out.push({ portName, artifact: a });
      }
    }
    return out;
  }, [selectedArtifacts, availableArtifacts]);

  const hasFailedUpstream = failedSelections.length > 0;

  // Stable identity for the current failed-selection set. Used both
  // as the acknowledgement key (Rupert PR-300 r1 atomic override) and
  // as the comparison target when deriving overrideChecked below.
  const failedHashesKey = React.useMemo(
    () =>
      failedSelections
        .map((s) => `${s.portName}:${s.artifact.content_hash}`)
        .sort()
        .join("|"),
    [failedSelections]
  );

  // Override is "effective" only when the user acknowledged the
  // EXACT same failed-selection set that's currently selected. A
  // swap (deselect / replace / add another failed) silently
  // invalidates the prior acknowledgement — no useEffect race, no
  // transient enabled-render. Per Rupert PR-300 r1.
  const overrideChecked = hasFailedUpstream && acknowledgedFailedHashesKey === failedHashesKey;

  // A required input port is "missing" when the manifest declares it
  // required AND `selectedArtifacts` has no entry for it. The Submit
  // button reflects this so the UI doesn't allow a submission the
  // backend will refuse with 400 missing_required_input_port.
  const missingRequiredPorts = React.useMemo(() => {
    if (!manifestInputs) return [];
    return Object.entries(manifestInputs)
      .filter(([name, port]) => port.required !== false && !selectedArtifacts[name])
      .map(([name]) => name);
  }, [manifestInputs, selectedArtifacts]);
  const hasMissingRequired = missingRequiredPorts.length > 0;

  // Unified manifest-unavailable guard (Rupert PR-300 r2): the
  // required-port + cardinality checks all key off ``manifestInputs``.
  // Whenever it's undefined — initial fetch in flight, fetch errored,
  // payload succeeded but failed the defensive shape check — the
  // guards silently fail open. Backend slice 2 still rejects, but
  // the UI was allowing doomed submissions. ``manifestUnavailable``
  // collapses the three causes into one disabled condition with
  // distinct messaging per cause.
  const manifestUnavailable = Boolean(selectedTile) && !manifestInputs;
  const manifestUnavailableReason: "loading" | "error" | "invalid" | null = (() => {
    if (!manifestUnavailable) return null;
    if (manifest.isLoading) return "loading";
    if (manifest.isError) return "error";
    return "invalid";
  })();

  const onSelectArtifact = (portName: string, contentHash: string | undefined): void => {
    setSelectedArtifacts((prev) => {
      if (contentHash === undefined) {
        const { [portName]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [portName]: contentHash };
    });
  };

  if (project.isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }
  if (project.isError) {
    const isNotFound = project.error instanceof ApiError && project.error.status === 404;
    return (
      <EmptyState
        title={isNotFound ? "Project not found." : "Could not load project."}
        description={
          isNotFound
            ? `No project with slug ${slug ? `'${slug}'` : "(empty)"}.`
            : project.error instanceof Error
              ? project.error.message
              : "Unknown error."
        }
      />
    );
  }
  if (!project.data) return <></>;

  const onSelectTile = (name: string): void => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("tile", name);
      return params;
    });
  };

  const onOverrideChange = (next: boolean): void => {
    // Per Rupert PR-300 r1: store the failed-set identity the user
    // is acknowledging, not a bare bool. A subsequent selection
    // change (deselect / swap to a different failed) invalidates the
    // acknowledgement automatically because failedHashesKey no
    // longer matches.
    setAcknowledgedFailedHashesKey(next ? failedHashesKey : null);
  };

  const onSubmit = (): void => {
    if (!selectedTile) return;
    // overrideChecked is already the derived effective value
    // (true only when the user acknowledged the EXACT current
    // failed set). No stale-state race.
    submit.mutate(
      {
        tile_name: selectedTile,
        parameters: values,
        input_artifacts: selectedArtifacts,
        override_failed_upstream: overrideChecked,
        // Omitted when the user left the declared class untouched, so
        // the request takes the default-safe no-override path.
        resources: resourceOverride,
        // fs#1207: file the run under the project this page is in.
        project_id: project.data?.project_id,
      },
      {
        onSuccess: (data) => {
          toast.success(`Run submitted: ${data.run_id}`);
          if (data.failed_input_artifacts_overridden.length > 0) {
            toast.warning(
              `Override exercised: ${data.failed_input_artifacts_overridden.length} failed-tagged upstream(s) accepted.`,
              { duration: 8_000 }
            );
          }
          navigate(`/runs/${data.run_id}`);
        },
        onError: (err) => {
          toast.error(`Submission failed: ${(err as Error).message}`);
        },
      }
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Link to="/projects" className="hover:text-foreground">
          Projects
        </Link>
        <span aria-hidden>·</span>
        <Link to={`/projects/${project.data.slug}`} className="hover:text-foreground">
          {project.data.slug}
        </Link>
        <span aria-hidden>·</span>
        <Link to={`/projects/${project.data.slug}/submit`} className="hover:text-foreground">
          submit
        </Link>
        <span aria-hidden>·</span>
        <span>tile</span>
      </div>

      <PageHeader
        title="Submit a Tile"
        description="Pick a Tile from the catalog and supply its declared parameters. Cost updates live as you edit."
      />

      <ExecutionModeNotice mode={executionMode.mode} isLoading={executionMode.isPending} />

      <div className="grid gap-6 lg:grid-cols-[1.2fr_2fr_1fr]">
        <section className="flex flex-col gap-3" aria-labelledby="tile-picker-heading">
          <h2 id="tile-picker-heading" className="label-micro text-muted-foreground">
            Catalog
          </h2>
          <TilePicker
            tiles={tiles.data}
            selected={selectedTile}
            onSelect={onSelectTile}
            isLoading={tiles.isLoading}
          />
        </section>

        <section className="flex flex-col gap-4" aria-labelledby="manifest-form-heading">
          <div className="flex items-center justify-between gap-2">
            <h2 id="manifest-form-heading" className="label-micro text-muted-foreground">
              Submission
            </h2>
            {selectedTile && (
              <span className="font-mono text-xs text-muted-foreground">{selectedTile}</span>
            )}
          </div>

          {selectedTile && (
            <Card className="p-card">
              <h3 className="label-micro mb-3 text-muted-foreground">Inputs</h3>
              <ArtifactInputPicker
                inputs={manifestInputs}
                artifacts={availableArtifacts}
                selected={selectedArtifacts}
                onChange={onSelectArtifact}
                onUploadMolecule={(file, declaredCharge) =>
                  uploadMolecule.mutateAsync({ file, declaredCharge })
                }
                isLoading={manifest.isLoading || artifacts.isLoading}
              />
              <SelectedInputPreview selected={selectedArtifacts} />
            </Card>
          )}

          <Card className="p-card">
            <h3 className="label-micro mb-3 text-muted-foreground">Parameters</h3>
            <ManifestForm
              manifest={manifest.data?.manifest}
              values={values}
              onChange={setValues}
              isLoading={manifest.isLoading && Boolean(selectedTile)}
            />
          </Card>

          {selectedTile && declaredResources && (
            <Card className="p-card">
              <h3 className="label-micro mb-3 text-muted-foreground">Resources</h3>
              <ResourceClassControl
                declared={declaredResources}
                capability={resourceCap}
                value={resourceOverride}
                onChange={setResourceOverride}
              />
            </Card>
          )}

          <FailedUpstreamBanner
            failedSelections={failedSelections}
            overrideChecked={overrideChecked}
            onOverrideChange={onOverrideChange}
          />

          {manifestUnavailableReason === "loading" && (
            <p role="status" className="text-xs italic text-muted-foreground" aria-live="polite">
              Loading Tile manifest…
            </p>
          )}
          {manifestUnavailableReason === "error" && (
            <div
              role="alert"
              className="rounded-md border-l-4 border-state-failed-validation-fg bg-state-failed-validation-fg/5 p-3 text-xs"
            >
              <p className="font-semibold text-state-failed-validation-fg">
                Manifest could not be loaded for {selectedTile}.
              </p>
              <p className="text-foreground">
                The submit guard rails (required-port + failed-upstream) can&apos;t run without the
                manifest; Submit is disabled until the manifest fetch recovers. Retry or pick a
                different Tile.
              </p>
            </div>
          )}
          {manifestUnavailableReason === "invalid" && (
            <div
              role="alert"
              className="rounded-md border-l-4 border-state-failed-validation-fg bg-state-failed-validation-fg/5 p-3 text-xs"
            >
              <p className="font-semibold text-state-failed-validation-fg">
                Manifest for {selectedTile} is malformed.
              </p>
              <p className="text-foreground">
                The manifest payload loaded but its <span className="font-mono">inputs</span> block
                doesn&apos;t match the expected shape, so the submit guard rails can&apos;t run.
                Submit is disabled. This is usually a server-side bug — refresh, or pick a different
                Tile.
              </p>
            </div>
          )}

          <div className="flex items-center justify-end gap-3">
            <Link
              to={`/projects/${project.data.slug}`}
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              Cancel
            </Link>
            <Button
              onClick={onSubmit}
              disabled={
                !selectedTile ||
                submit.isPending ||
                manifestUnavailable ||
                hasMissingRequired ||
                (hasFailedUpstream && !overrideChecked)
              }
              title={
                manifestUnavailableReason === "loading"
                  ? "Loading manifest…"
                  : manifestUnavailableReason === "error"
                    ? "Manifest failed to load; cannot validate inputs"
                    : manifestUnavailableReason === "invalid"
                      ? "Manifest payload is malformed; cannot validate inputs"
                      : hasMissingRequired
                        ? `Missing required input port(s): ${missingRequiredPorts.join(", ")}`
                        : undefined
              }
              className="gap-1.5"
            >
              {submit.isPending ? "Submitting…" : "Submit run"}
              {submit.isPending ? (
                <ArrowRight className="h-3.5 w-3.5 motion-safe:animate-arc-spin" aria-hidden />
              ) : (
                <Send className="h-3.5 w-3.5" aria-hidden />
              )}
            </Button>
          </div>
        </section>

        <CostPreviewPanel
          tileName={selectedTile}
          parameters={values}
          resources={resourceOverride}
        />
      </div>
    </div>
  );
}
